package com.eladfinish.bible.contest.screens.verseWidget.ui

import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Info
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.FilterChip
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.MenuAnchorType
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Surface
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.colorAccent
import com.eladfinish.bible.contest.domain.BooksRepository
import com.eladfinish.bible.contest.features.verseWidget.VerseWidgetConfig
import com.eladfinish.bible.contest.features.verseWidget.VerseWidgetEntryPoint
import com.eladfinish.bible.contest.features.verseWidget.VerseWidgetOrder
import com.eladfinish.bible.contest.features.verseWidget.VerseWidgetSource
import com.eladfinish.bible.contest.features.verseWidget.WidgetVerse
import com.eladfinish.bible.contest.lifecycle.OnResumeListener
import com.eladfinish.bible.contest.platform.isIos
import com.eladfinish.bible.contest.screens.verseWidget.VerseWidgetAction
import com.eladfinish.bible.contest.screens.verseWidget.VerseWidgetState
import com.eladfinish.bible.contest.screens.verseWidget.VerseWidgetStore
import com.eladfinish.bible.contest.strokeColor
import com.eladfinish.bible.contest.ui.common.PreviewTheme
import com.eladfinish.bible.contest.ui.components.buttons.PrimaryActionButton
import com.eladfinish.bible.contest.ui.components.verticalScrollWithScrollbar
import com.eladfinish.bible.contest.ui.extensions.navigationBarInsetPadding
import com.eladfinish.bible.contest.verse_widget_active
import com.eladfinish.bible.contest.verse_widget_add
import com.eladfinish.bible.contest.verse_widget_book_title
import com.eladfinish.bible.contest.verse_widget_example_button
import com.eladfinish.bible.contest.verse_widget_got_it
import com.eladfinish.bible.contest.verse_widget_instructions_android
import com.eladfinish.bible.contest.verse_widget_instructions_ios
import com.eladfinish.bible.contest.verse_widget_instructions_title
import com.eladfinish.bible.contest.verse_widget_interval_10m
import com.eladfinish.bible.contest.verse_widget_interval_12h
import com.eladfinish.bible.contest.verse_widget_interval_15m
import com.eladfinish.bible.contest.verse_widget_interval_1d
import com.eladfinish.bible.contest.verse_widget_interval_1h
import com.eladfinish.bible.contest.verse_widget_interval_30m
import com.eladfinish.bible.contest.verse_widget_interval_3h
import com.eladfinish.bible.contest.verse_widget_interval_5m
import com.eladfinish.bible.contest.verse_widget_interval_title
import com.eladfinish.bible.contest.verse_widget_mikud_empty
import com.eladfinish.bible.contest.verse_widget_nikud
import com.eladfinish.bible.contest.verse_widget_order_random
import com.eladfinish.bible.contest.verse_widget_order_sequential
import com.eladfinish.bible.contest.verse_widget_order_title
import com.eladfinish.bible.contest.verse_widget_preview
import com.eladfinish.bible.contest.verse_widget_source_all
import com.eladfinish.bible.contest.verse_widget_source_book
import com.eladfinish.bible.contest.verse_widget_source_mikud
import com.eladfinish.bible.contest.verse_widget_source_parasha
import com.eladfinish.bible.contest.verse_widget_source_title
import com.eladfinish.bible.contest.verse_widget_tap_hint
import com.eladfinish.bible.contest.verse_widget_teamim
import dev.icerock.moko.resources.StringResource
import dev.icerock.moko.resources.compose.colorResource
import dev.icerock.moko.resources.compose.stringResource
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun VerseWidgetScreen(
    entryPoint: VerseWidgetEntryPoint,
    verseWidgetStore: VerseWidgetStore = koinViewModel(),
) {
    val state by verseWidgetStore.observeState().collectAsState()

    // Once per visit, not again when the screen is recreated by a rotation.
    var isOpenReported by rememberSaveable { mutableStateOf(false) }
    LaunchedEffect(Unit) {
        if (!isOpenReported) {
            isOpenReported = true
            verseWidgetStore.dispatch(VerseWidgetAction.Opened(entryPoint))
        }
    }

    // The widget may have been added or removed on the home screen meanwhile
    OnResumeListener { verseWidgetStore.dispatch(VerseWidgetAction.Resumed) }

    VerseWidgetContent(state = state, onAction = verseWidgetStore::dispatch)
}

@Composable
internal fun VerseWidgetContent(
    state: VerseWidgetState,
    onAction: (VerseWidgetAction) -> Unit,
) {
    val config = state.config

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .verticalScrollWithScrollbar()
            .padding(horizontal = 16.dp, vertical = 12.dp)
            .navigationBarInsetPadding(),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        WhatIsAWidgetButton(onClick = { onAction(VerseWidgetAction.ExampleClicked) })

        VerseWidgetPreview(state = state)

        SectionTitle(stringResource(SharedRes.strings.verse_widget_source_title))
        ChipRow {
            VerseWidgetSource.entries.forEach { source ->
                SelectableChip(
                    text = stringResource(source.label()),
                    selected = config.source == source,
                    onClick = { onAction(VerseWidgetAction.SourceSelected(source)) },
                )
            }
        }

        if (config.source == VerseWidgetSource.BOOK) {
            BookPicker(
                selectedBookNumber = config.bookNumber,
                onBookSelected = { onAction(VerseWidgetAction.BookSelected(it)) },
            )
        }

        SectionTitle(stringResource(SharedRes.strings.verse_widget_order_title))
        ChipRow {
            VerseWidgetOrder.entries.forEach { order ->
                SelectableChip(
                    text = stringResource(order.label()),
                    selected = config.order == order,
                    onClick = { onAction(VerseWidgetAction.OrderSelected(order)) },
                )
            }
        }

        SectionTitle(stringResource(SharedRes.strings.verse_widget_interval_title))
        ChipRow {
            VerseWidgetConfig.INTERVALS.forEach { minutes ->
                SelectableChip(
                    text = stringResource(intervalLabel(minutes)),
                    selected = config.intervalMinutes == minutes,
                    onClick = { onAction(VerseWidgetAction.IntervalSelected(minutes)) },
                )
            }
        }

        SwitchRow(
            text = stringResource(SharedRes.strings.verse_widget_nikud),
            checked = config.showNikud,
            onCheckedChange = { onAction(VerseWidgetAction.NikudChanged(it)) },
        )

        // The cantillation marks are drawn only over the nikud
        SwitchRow(
            text = stringResource(SharedRes.strings.verse_widget_teamim),
            checked = config.showTeamim && config.showNikud,
            enabled = config.showNikud,
            onCheckedChange = { onAction(VerseWidgetAction.TeamimChanged(it)) },
        )

        PrimaryActionButton(
            modifier = Modifier.fillMaxWidth(),
            text = stringResource(SharedRes.strings.verse_widget_add),
            onClick = { onAction(VerseWidgetAction.AddClicked) },
        )

        if (state.isWidgetInstalled) {
            Text(
                modifier = Modifier.fillMaxWidth(),
                text = stringResource(SharedRes.strings.verse_widget_active),
                style = MaterialTheme.typography.bodyMedium,
                textAlign = TextAlign.Center,
            )
        }

        Text(
            modifier = Modifier.fillMaxWidth(),
            text = stringResource(SharedRes.strings.verse_widget_tap_hint),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            textAlign = TextAlign.Center,
        )
    }

    if (state.isInstructionsVisible) {
        InstructionsDialog(onDismiss = { onAction(VerseWidgetAction.InstructionsDismissed) })
    }

    if (state.isExampleVisible) {
        VerseWidgetExample(
            imageUrl = state.exampleImageUrl,
            onDismiss = { onAction(VerseWidgetAction.ExampleDismissed) },
        )
    }
}

/** For those who do not know the word: a tap shows a picture of the widget on a home screen. */
@Composable
private fun ColumnScope.WhatIsAWidgetButton(onClick: () -> Unit) {
    TextButton(
        modifier = Modifier.align(Alignment.CenterHorizontally),
        onClick = onClick,
    ) {
        Icon(
            modifier = Modifier.size(18.dp),
            imageVector = Icons.Default.Info,
            contentDescription = null,
        )
        Spacer(Modifier.width(8.dp))
        Text(stringResource(SharedRes.strings.verse_widget_example_button))
    }
}

@Composable
private fun VerseWidgetPreview(state: VerseWidgetState) {
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .border(
                width = 1.dp,
                color = colorResource(SharedRes.colors.strokeColor),
                shape = RoundedCornerShape(16.dp),
            ),
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.surfaceVariant,
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            Text(
                text = stringResource(SharedRes.strings.verse_widget_preview),
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )

            val preview = state.preview
            when {
                preview != null -> {
                    Text(
                        text = preview.text,
                        style = MaterialTheme.typography.bodyLarge,
                        fontSize = 20.sp,
                        lineHeight = 30.sp,
                        textAlign = TextAlign.Center,
                    )
                    Text(
                        text = preview.reference,
                        style = MaterialTheme.typography.labelLarge,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }

                state.config.source == VerseWidgetSource.MIKUD -> Text(
                    text = stringResource(SharedRes.strings.verse_widget_mikud_empty),
                    style = MaterialTheme.typography.bodyMedium,
                    textAlign = TextAlign.Center,
                )
            }
        }
    }
}

@Composable
private fun SectionTitle(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.titleSmall,
    )
}

/** The chips in one line that scrolls sideways when they do not fit. */
@Composable
private fun ChipRow(content: @Composable RowScope.() -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .horizontalScroll(rememberScrollState()),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        content = content,
    )
}

@Composable
private fun SwitchRow(
    text: String,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    enabled: Boolean = true,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Text(
            text = text,
            style = MaterialTheme.typography.bodyLarge,
            color = if (enabled) Color.Unspecified else MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Switch(
            checked = checked,
            onCheckedChange = onCheckedChange,
            enabled = enabled,
            colors = SwitchDefaults.colors(
                checkedTrackColor = colorResource(SharedRes.colors.colorAccent),
            ),
        )
    }
}

@Composable
private fun SelectableChip(text: String, selected: Boolean, onClick: () -> Unit) {
    FilterChip(
        selected = selected,
        onClick = onClick,
        label = { Text(text) },
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun BookPicker(selectedBookNumber: Int, onBookSelected: (Int) -> Unit) {
    var isExpanded by rememberSaveable { mutableStateOf(false) }

    ExposedDropdownMenuBox(
        modifier = Modifier.fillMaxWidth(),
        expanded = isExpanded,
        onExpandedChange = { isExpanded = it },
    ) {
        OutlinedTextField(
            modifier = Modifier
                .menuAnchor(type = MenuAnchorType.PrimaryNotEditable, enabled = true)
                .fillMaxWidth(),
            readOnly = true,
            value = BooksRepository.bookNamesHebrew.getOrNull(selectedBookNumber - 1).orEmpty(),
            onValueChange = {},
            label = { Text(stringResource(SharedRes.strings.verse_widget_book_title)) },
            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = isExpanded) },
        )

        ExposedDropdownMenu(
            expanded = isExpanded,
            onDismissRequest = { isExpanded = false },
        ) {
            BooksRepository.bookNamesHebrew.forEachIndexed { index, bookName ->
                DropdownMenuItem(
                    text = { Text(bookName) },
                    onClick = {
                        onBookSelected(index + 1)
                        isExpanded = false
                    },
                )
            }
        }
    }
}

@Composable
private fun InstructionsDialog(onDismiss: () -> Unit) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(stringResource(SharedRes.strings.verse_widget_instructions_title)) },
        text = {
            Text(
                stringResource(
                    if (isIos) SharedRes.strings.verse_widget_instructions_ios
                    else SharedRes.strings.verse_widget_instructions_android
                )
            )
        },
        confirmButton = {
            TextButton(onClick = onDismiss) {
                Text(stringResource(SharedRes.strings.verse_widget_got_it))
            }
        },
    )
}

private fun VerseWidgetSource.label(): StringResource = when (this) {
    VerseWidgetSource.MIKUD -> SharedRes.strings.verse_widget_source_mikud
    VerseWidgetSource.BOOK -> SharedRes.strings.verse_widget_source_book
    VerseWidgetSource.PARASHA -> SharedRes.strings.verse_widget_source_parasha
    VerseWidgetSource.ALL -> SharedRes.strings.verse_widget_source_all
}

private fun VerseWidgetOrder.label(): StringResource = when (this) {
    VerseWidgetOrder.RANDOM -> SharedRes.strings.verse_widget_order_random
    VerseWidgetOrder.SEQUENTIAL -> SharedRes.strings.verse_widget_order_sequential
}

private fun intervalLabel(minutes: Int): StringResource = when (minutes) {
    5 -> SharedRes.strings.verse_widget_interval_5m
    10 -> SharedRes.strings.verse_widget_interval_10m
    15 -> SharedRes.strings.verse_widget_interval_15m
    30 -> SharedRes.strings.verse_widget_interval_30m
    60 -> SharedRes.strings.verse_widget_interval_1h
    180 -> SharedRes.strings.verse_widget_interval_3h
    720 -> SharedRes.strings.verse_widget_interval_12h
    else -> SharedRes.strings.verse_widget_interval_1d
}

@Preview
@Composable
private fun VerseWidgetScreenPreview() {
    PreviewTheme {
        VerseWidgetContent(
            state = VerseWidgetState(
                config = VerseWidgetConfig(source = VerseWidgetSource.BOOK),
                preview = WidgetVerse(
                    text = "בְּרֵאשִׁית בָּרָא אֱלֹהִים אֵת הַשָּׁמַיִם וְאֵת הָאָרֶץ",
                    reference = "בראשית א', א'",
                    bookNumber = 1,
                    chapter = 1,
                    verse = 1,
                ),
                installedCount = 1,
                canRequestPin = true,
            ),
            onAction = {},
        )
    }
}
